import { useMemo } from 'react';
import { Trans } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { cn } from '@/lib/utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { AwardIcon, InfoIcon } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';

const useColumnDefBestSeller = (): ExtendedColumnDef<DealItemWithVersions> => {
  const { setColumnSort } = useSetColumnSort();
  const isOfferLocked = useIsOfferLocked();

  const accessorKey: keyof DealItem = 'isBestSeller';

  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'BestSeller',
      accessorFn: (row) => row.activeVersion?.bestSellerQuantitySold ?? null,
      aggregationFn: 'sum',
      sortingFn: 'basic',
      header: () => {
        return (
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-center">
                  <InfoIcon className="h-4 w-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <Trans
                  i18nKey="kpi.best_seller_tooltip"
                  components={{ bold: <span className="font-semibold" /> }}
                />
              </TooltipContent>
            </Tooltip>
            <AwardIcon className="h-4 w-4 text-yellow-400" />
            <div onClick={() => setColumnSort(accessorKey)}>
              <SortIcon pointer="isBestSeller" />
            </div>
          </div>
        );
      },
      cell: ({ row, getValue }) => {
        const isParent = row.subRows.length > 1;
        const isBestSeller =
          row.original?.activeVersion.isBestSeller ??
          row.subRows.some(
            (subRow) => subRow.original.activeVersion.isBestSeller
          );

        const displayCount = getValue() as number | null;

        return (
          <div
            className={cn(
              'flex w-full flex-col items-center space-y-2 p-4 pl-3 text-left',
              isParent && 'cursor-pointer',
              isOfferLocked && 'opacity-50'
            )}
          >
            {isBestSeller && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center gap-1">
                    {displayCount != null && (
                      <span className="min-w-[24px] text-right text-sm">
                        {displayCount}
                      </span>
                    )}
                    <AwardIcon className="h-4 w-4 text-yellow-400" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {displayCount != null ? (
                      <Trans
                        i18nKey="kpi.best_seller_infos"
                        values={{
                          count: displayCount,
                        }}
                        components={{
                          bold: <span className="font-semibold" />,
                        }}
                      />
                    ) : (
                      <Trans
                        i18nKey="kpi.best_seller_tooltip"
                        components={{
                          bold: <span className="font-semibold" />,
                        }}
                      />
                    )}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      },
      size: 100,
    }),
    [setColumnSort, isOfferLocked]
  );
};

export default useColumnDefBestSeller;
